import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../api-client/MyApiClient';
import { sports } from '../../../components/Util/enum';
import { nomiSport, prodottoDelloSport, sportDelProdotto } from '../../../components/Util/sportProdotto';
import { AvvisoCorsoFuoriDate } from '../../../components/Util/corsoFuoriDate';
import { STATO_ARCHIVIATO, SelettoreArchivio, nellaVista, useVistaArchivio } from '../../../components/Util/archivioProdotti';
import { formattaPrezzo } from '../../../components/Util/prezzi';

/*
 * Corsi — elenco corsi: la stessa pagina dell'Elenco corsi (colonne, ricerca, filtri a chip,
 * freccia di apertura) e gli stessi dati veri del gestionale. Cambia solo dove porta la riga:
 * alla scheda corso della nuova gestione.
 */

const STATO_OPTIONS = ['Bozza', 'Attivo', 'Non attivo'];

export const CorsiListPage = () => {
    const navigate = useNavigate();
    const [data_json, setDataJson] = useState(null);
    const [loadingShowed, setLoadingShowed] = useState(false);
    const [sportFilter, setSportFilter] = useState('');
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);
    // In uso / Archivio (TASK #7): in archivio solo i corsi messi su «Archiviato»
    const [vista, setVista] = useVistaArchivio();

    // Tutti gli sport di tutti i corsi: un corso con piu' sport compare sotto ciascuno
    const sportOptions = useMemo(
        () => [...new Set((data_json || []).flatMap((row) => sportDelProdotto(row)))]
            .map((codice) => ({ value: codice, label: sports.find((s) => s.value === codice)?.label || codice }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        [data_json],
    );
    // Un filtro ricordato che non corrisponde piu' a nessuno sport (prima erano i nomi) non filtra
    const sportScelto = sportOptions.some((o) => o.value === sportFilter) ? sportFilter : '';

    const filteredData = useMemo(
        () =>
            data_json &&
            data_json.filter(
                (row) =>
                    (!sportScelto || prodottoDelloSport(row, sportScelto)) &&
                    filtroMultiplo(statoFilter, STATO_OPTIONS, row.status_label?.label) &&
                    nellaVista(vista, row),
            ),
        [data_json, sportScelto, statoFilter, vista],
    );

    const apriScheda = (row) => navigate(`/struttura/corsi/${row.id}`);

    // Prezzi di nuovo in elenco (22/10/2026), come negli abbonamenti; soci/tesserati solo per ASD e SSD
    let clubNature = null;
    try { clubNature = JSON.parse(sessionStorage.getItem('userDetails'))?.clubNature; } catch (e) { clubNature = null; }
    const mostraPrezzoSoci = clubNature === 'ASD' || clubNature === 'SSD';

    const data_column = [
        {
            Header: 'Nome',
            Footer: 'Nome',
            // La ricerca e' per nome del corso (il maestro sta nelle squadre, non nel corso)
            id: 'name',
            accessor: 'name',
            // Triangolino se le date del corso sono passate: non si vede nell'app e non si acquista
            Cell: ({ row }) => <>{row.original.name}<AvvisoCorsoFuoriDate corso={row.original} /></>,
            // Il nome del corso è la colonna che si legge: ha più spazio delle altre
            larghezza: '38%',
        },
        {
            Header: 'N. Squadre',
            Footer: 'N. Squadre',
            accessor: 'squadre',
            align: 'center',
            disableGlobalFilter: true,
            Cell: ({ value }) => value || 0,
            disableFilters: true,
        },
        {
            Header: 'N. Vendite',
            Footer: 'N. Vendite',
            accessor: 'purchasesCount',
            align: 'center',
            disableGlobalFilter: true,
            Cell: ({ value }) => value || 0,
            disableFilters: true,
        },
        {
            Header: 'Sport',
            Footer: 'Sport',
            accessor: 'sportNomi',
            disableGlobalFilter: true,
        },
        {
            Header: 'Prezzo standard',
            Footer: 'Prezzo standard',
            accessor: 'standard_price',
            disableGlobalFilter: true,
        },
        ...(mostraPrezzoSoci ? [{
            Header: 'Prezzo soci/tesserati',
            Footer: 'Prezzo soci/tesserati',
            accessor: 'federated_price',
            disableGlobalFilter: true,
        }] : []),
        {
            Header: 'Stato',
            Footer: 'Stato',
            accessor: 'status_label',
            disableGlobalFilter: true,
            Cell: ({ value }) => (
                <div className="d-flex align-items-center">
                    <i className={`fa fa-circle me-1 ${value ? value.class : ''}`}></i>
                    {value ? value.label : ''}
                </div>
            ),
            disableFilters: true,
        },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (!tokenDetailsString) return;
                const token = JSON.parse(tokenDetailsString).idToken;
                const apiClient = new MyApiClient();

                setLoadingShowed(true);
                // Le squadre si chiedono tutte in una volta e si contano per corso: una
                // chiamata per riga sarebbe stata un'attesa per ogni corso in elenco.
                const [bookingCourses, squadre] = await Promise.all([
                    apiClient.getBookingCourses(token),
                    apiClient.getBookingCourseTeams(token),
                ]);
                setLoadingShowed(false);

                if (!bookingCourses) return;
                const squadrePerCorso = {};
                (Array.isArray(squadre) ? squadre : []).forEach((t) => {
                    const corso = t.bookingCourseId || t.courseId;
                    if (corso) squadrePerCorso[corso] = (squadrePerCorso[corso] || 0) + 1;
                });
                setDataJson(bookingCourses.map((element) => {
                    const status = element.status === 'DRAFT' ? { label: 'Bozza', class: 'text-secondary' }
                        : element.status === 'PURCHASABLE' ? { label: 'Attivo', class: 'text-success' }
                        : element.status === 'CANCELLED' ? { label: 'Non attivo', class: 'text-warning' }
                        : null;
                    return {
                        id: element.id,
                        name: element.name,
                        sportType: element.sportType,
                        sportTypes: element.sportTypes,
                        sportNomi: nomiSport(element),
                        type: element.type,
                        status: element.status,
                        status_label: element.archived ? STATO_ARCHIVIATO : status,
                        squadre: squadrePerCorso[element.id] || 0,
                        purchasesCount: element.purchasesCount,
                        standard_price: formattaPrezzo(element.standard_price, { conZero: false }),
                        federated_price: formattaPrezzo(element.federated_price, { conZero: false }),
                        coachName: element.coachName,
                        endDate: element.endDate,
                        archivio: !!element.archived,
                    };
                }));
            } catch (error) {
                setLoadingShowed(false);
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <TabellaLista
                pageList={true}
                hasAddMore={true}
                linkAddMore={'/struttura/corsi/nuovo'}
                labelAddMore={'Nuovo corso'}
                hasSearch={true}
                searchPlaceholder={'Cerca per nome'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters">
                        <ChipFilter label="Sport" options={sportOptions} value={sportFilter} onChange={setSportFilter} asDropdown />
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                        <SelettoreArchivio value={vista} onChange={setVista} />
                    </div>
                }
                data_column={data_column}
                data_json={filteredData}
                openColumn={'name'}
                openRow={(row) => apriScheda(row)}
                sort_by={'name'}
                sort_type={'asc'}
            />
        </>
    );
};

export default CorsiListPage;
